import { useCallback, useEffect, useState } from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { T, useT } from "../i18n";
import { Meldung } from "../bausteine";
import { useSitzung } from "./Sitzung";
import { useRuf } from "./Ruf";
import { rechtZiel } from "./rechte";
import { umleitungNehmen, umleitungsSatz } from "./umleitung";
import { Leiste } from "./Leiste";
import { Handyleiste, useHandyleiste } from "./Handyleiste";
import { Telefonmarke } from "./Telefonmarke";
import { Anlegeblatt } from "./Anlegeblatt";
import { useGeraet } from "./geraet";
import { KopfRaum } from "./Kopf";
import { NebenleisteRaum } from "./Nebenleiste";
import { Farbraum } from "./Farben";
import { Fassungshinweis } from "../bausteine/Offline";
import { Merkliste } from "../bausteine/Merkliste";
import { Merksperre } from "../bausteine/Merksperre";
import { Touchdiagnose } from "../bausteine/Touchdiagnose";
import { Ziehauffrischen } from "../bausteine/Ziehauffrischen";
import { Zeichen } from "../bausteine/Zeichen";
import { Nachschlagen } from "../masken/Nachschlagen";
import { OrtsaufnahmeFenster } from "../masken/vorgaenge/OrtsaufnahmeAnlegen";
import { nachschlagenUmschalten } from "./nachschlagen";
import { Suche, SucheKnopf } from "./Suche";
import { Benutzermenue, HausAnlegen, KopfAktualisieren, KopfHilfe } from "./Kopfwerkzeuge";
import { Glocke } from "../bausteine/Glocke";
import { Anmeldung } from "../masken/Anmeldung";
import { LogoBild } from "./LogoBild";
import { EINGEBAUT } from "./erscheinungsbild";

/* ══════════════════════════════════════════════════════════════════
   Die Schale: Kopfzeile oben, Seitenleiste links, Maske rechts.

   Logo und Maskenkopf sitzen auf einer Linie — sonst wirkt die
   Anwendung wie zwei Programme nebeneinander. Kopf und Seitenleiste
   haben denselben Ton (`--seite`), die Maske darunter `--flaeche`.

   Vor der Anmeldung gibt es nichts davon: keine Navigation, keinen
   Benutzerfuß, keine Kopfzeile. Die Anmeldung füllt den Bildschirm.
   ══════════════════════════════════════════════════════════════════ */

export function Schale() {
  const t = useT();
  const { angemeldet, prueft, ich, darfAuf } = useSitzung();
  const { location } = useRouterState();
  const ruf = useRuf();
  // In den Einstellungen schiebt die Seitenleiste auf ihre zweite Bahn:
  // dieselbe Leiste, andere Navigation, oben der Weg zurueck zur Arbeit
  // (28-designkonzept.md, „Die Einstellungen sind die zweite Bahn der
  // Seitenleiste"). Wer dort ist, richtet ein und arbeitet nicht — der
  // Wechsel soll sichtbar sein, aber er soll nichts wegnehmen.
  const richtetEin = location.pathname.startsWith("/einstellungen");
  // Am Telefon gliedert eine Leiste unten die Handy-Masken, je nach Rolle
  // (`Handyleiste.tsx`). Steht keine an — falscher Weg oder keine Rolle —,
  // ist die Liste leer und der Maskenraum braucht unten keinen Platz.
  const handywege = useHandyleiste();
  const geraet = useGeraet();
  const telefon = geraet === "telefon";
  /* **Erst das Recht, dann die Maske** (K-09, T-04, R-32, S-36; Durchgang
     03.10.2026). Der Wegwächter im Router kennt die Rolle erst, wenn sie
     neben dem React-Baum steht (`rollenMerken`, eine Wirkung nach dem
     Zeichnen) — in diesem einen Zeichnen war die Maske schon da und fragte
     ihre Daten ab: Kalle löste auf `/kunden/10015` drei 403 aus, bevor er
     auf „Heute" landete. Ein Weg, den die Rolle nicht darf, zeichnet hier
     nichts; die Umleitung folgt einen Takt später.

     **Und es wird gesagt, warum.** Bis dahin war es eine stille Umleitung —
     wer `/abrechnung` tippte, stand ohne Satz auf „Heute". */
  const pfad = location.pathname;
  // Beide Fragen müssen „nein" sagen: die des Wächters (Rolle, Einstieg) und
  // die mit den Rechten dieses Menschen — ein einzeln gegebenes Recht gilt.
  const gesperrt = !prueft && ich !== null && pfad !== "/" && !darfAuf(pfad)
    && rechtZiel(ich.rollen, pfad, telefon) !== null;
  useEffect(() => {
    if (gesperrt) ruf("rechtFehlt", undefined, "acht");
  }, [gesperrt, pfad, ruf]);
  /* **Auf dem Tablet lässt sich die Seitenleiste einklappen** (17.09.2026,
     `stile/schale.css`, „Das Tablet"). Die Wahl gilt für dieses Gerät und
     überdauert den Tag — wer auf dem iPad die Planung breit haben will, will
     sie morgen auch. Seit Signal (07.10.2026) gilt sie auch am Schreibtisch;
     am Telefon gibt es keine Seitenleiste. */
  const [eingeklappt, setzeEingeklappt] = useState(() => leisteEingeklappt());
  const einklappen = () => {
    const neu = !eingeklappt;
    setzeEingeklappt(neu);
    leisteEingeklapptMerken(neu);
  };
  /* Seit Signal auch am Schreibtisch (Muster: Seitenleiste 224 → 56 px, nur
     die Zeichen). Am Telefon gibt es keine Seitenleiste. */
  const zu = !telefon && eingeklappt;
  /* **Eine Umleitung sagt, warum** (D-03, 03.10.2026). Der Wächter im
     Routenbaum legt den Grund ab (`schale/umleitung.ts`); hier wird er
     einmal gerufen, sobald die Maske steht, auf die er geschickt hat. */
  useEffect(() => {
    const u = umleitungNehmen();
    if (u && angemeldet) ruf(umleitungsSatz(u), undefined, "acht");
  }, [location.pathname, angemeldet, ruf]);
  /* ── Der Kopf der Maske steht im Maskenraum, auf jedem Gerät ─────────
     Bis zum 07.10.2026 hing er am Schreibtisch **in** der Kopfzeile und nur
     am Telefon darunter (zwei Ziele, je eines je Lage). Seit „Signal" ist
     die Kopfzeile eine Leiste, die sich nie ändert (docs/64, Paket 3); der
     Kopf der Maske steht überall als erste Zeile im Maskenraum. Damit gibt
     es nur noch ein Ziel. */
  const [kopfZiel, setzeKopfZiel] = useState<HTMLElement | null>(null);
  const [leisteZiel, setzeLeisteZiel] = useState<HTMLElement | null>(null);
  const [leisteOffen, setzeLeisteOffen] = useState(false);

  // Der Kopf der Maske wird per Portal hierher gehängt. Der Rückruf
  // läuft, sobald das Element steht — kein `useEffect` mit Suche im
  // Dokument, der beim ersten Bild ins Leere greift.
  const kopfMerken = useCallback((el: HTMLDivElement | null) => setzeKopfZiel(el), []);
  // Dasselbe für die zweite Bahn der Leiste (`Nebenleiste.tsx`).
  const leisteMerken = useCallback((el: HTMLDivElement | null) => setzeLeisteZiel(el), []);

  // Beim Maskenwechsel geht die Leiste auf dem Telefon wieder zu.
  const schliessen = useCallback(() => setzeLeisteOffen(false), []);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeLeisteOffen(false); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, []);

  /* ── Das Tastenkürzel fürs Nachschlagen ───────────────────────────
     Der Fall, für den das Blatt gebaut ist, ist ein klingelndes Telefon:
     Die Hand liegt ohnehin auf der Tastatur, und der Weg über die Leiste
     ist drei Zentimeter Maus zu viel.

     **`Alt` + `N`**, und zwar nicht `Strg`/`Cmd`: Die belegt der Browser
     (`Strg+N` ist ein neues Fenster), und ein Kürzel, das je nach Browser
     etwas anderes tut, ist keins. `Alt+N` ist in keinem der drei Browser
     vergeben, die hier laufen.

     **Nicht im Eingabefeld.** Wer in einem Textfeld steht, tippt — auch
     wenn er dabei `Alt` streift. Das gilt für `input`, `textarea` und
     alles, was `contenteditable` ist. */
  useEffect(() => {
    const kuerzel = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key !== "n" && e.key !== "N") return;
      const ziel = e.target as HTMLElement | null;
      const tippt = ziel?.isContentEditable
        || ["INPUT", "TEXTAREA", "SELECT"].includes(ziel?.tagName ?? "");
      if (tippt) return;
      e.preventDefault();
      nachschlagenUmschalten();
    };
    document.addEventListener("keydown", kuerzel);
    return () => document.removeEventListener("keydown", kuerzel);
  }, []);

  // Solange der Wiedereinstieg aus dem Sitzungscookie läuft, steht weder
  // Maske noch Anmeldung: Eine Anmeldung, die eine Zehntelsekunde
  // aufblitzt und wieder verschwindet, liest sich wie ein Fehler.
  if (prueft) return <div className="kasten" aria-busy="true" />;
  if (!angemeldet) return <Anmeldung />;

  return (
    <div className="kasten" data-leiste={zu ? "zu" : "auf"}>
      {/* Die globale Suche liegt über allem, auch über der Seitenleiste —
          sie ist ein Fenster, kein Blatt über der Maske. Zu zeichnet sie
          nichts und fragt nichts ab. */}
      <Suche />
      {/* Zeichnet nichts: legt die gepflegten Geschäftsfarben auf die
          Wurzel, bevor das Brett das erste Mal zeichnet. */}
      <Farbraum />
      {/* Ein Deployment kommt von selbst an (`pwa/anmelden.ts`); danach steht
          einmal kurz, dass es die neue Fassung ist. Zeichnet sonst nichts. */}
      <Fassungshinweis />
      <header className="haus-kopf">
        {/* ── Der Kopf seit „Signal" (07.10.2026, docs/64, Paket 3) ─────────
            Am Schreibtisch und auf dem Tablet eine dunkle Leiste über der
            ganzen Breite, wie im Muster: links Einklappen und Logo, daneben
            „Neu" und der Blitz, **die Suche mittig**, rechts die Zeichen und
            wer angemeldet ist (`Kopfwerkzeuge.tsx`). Der Name der Maske steht
            nicht mehr hier, sondern im Inhalt darunter — wie es am Telefon
            seit dem 18.09.2026 schon war (Begründung dort, unten).

            Am Telefon bleibt die feste Kopfleiste vom 18.09.2026: Marke
            links, Werkzeuge rechts. Luca: *„Oben die feste Leiste: Das macht
            das ganze Ding deutlich besser."* Was sie trägt, ändert sich nie;
            der Kopf der Maske steht im Maskenraum. */}
        <div className="haus-marke">
          {telefon ? (
            <>
              {/* **Signal, Paket 5 (Logo Variante A, Luca 07.10.2026):** die
                  Bildmarke einfarbig in Tinte und „Machine Office" als Schrift —
                  das volle Logo zerfaserte bei 40 px und biss sich mit dem
                  Grund. Auf einer Unterseite von „Mehr" steht stattdessen der
                  Rückweg dorthin (`Telefonmarke.tsx`). */}
              <Telefonmarke punkte={handywege} />
            </>
          ) : (
            <>
              {/* Einklappen auf 56 px (Muster: Seitenleiste 224/56). Unter
                  721 px ist die Leiste eine Schublade hinter dem Menüknopf
                  daneben; dort gibt es nichts einzuklappen. */}
              <button
                className="haus-einklappen"
                type="button"
                aria-expanded={!zu}
                aria-label={t(zu ? "nav.leisteAufklappen" : "nav.leisteEinklappen")}
                title={t(zu ? "nav.leisteAufklappen" : "nav.leisteEinklappen")}
                onClick={einklappen}
              >
                <Zeichen name="menue" />
              </button>
              <button
                className="haus-menue"
                type="button"
                aria-expanded={leisteOffen}
                aria-label={t(leisteOffen ? "nav.menueSchliessen" : "nav.menueOeffnen")}
                title={t(leisteOffen ? "nav.menueSchliessen" : "nav.menueOeffnen")}
                onClick={() => setzeLeisteOffen((x) => !x)}
              >
                <Zeichen name="menue" />
              </button>
              {/* Die dunkle Fassung ohne Leistungszeile — der Kopf ist immer
                  dunkel. Die volle Fassung steht auf der Anmeldung. */}
              <LogoBild className="haus-logo" art="dunkel" eingebaut={EINGEBAUT.kopf} alt={t("app.logoAlt")} />
            </>
          )}
        </div>
        {telefon ? null : <HausAnlegen />}
        {/* **Die Suche mittig** (Luca, 07.10.2026: *„Die Suchleiste oben soll
            ja mittig am besten sein"*). Ein Feld, das das Suchfenster öffnet
            (`Suche.tsx`); `Strg`/`⌘` + `K` öffnet es von überall. */}
        {telefon ? null : <div className="haus-suche"><SucheKnopf feld /></div>}

        {/* ── Nachschlagen — das Werkzeug der Anwendung ──────────────────
            Es stand bis zum 17.09.2026 in der Seitenleiste. Luca: *„Dieses
            Nachschlagen als extra Menüpunkt. Wieso? Wozu dann?"* — **Was in
            einer Navigationsliste steht, liest jeder als Ziel**, gleich was
            es tut. Die Kopfzeile ist der Ort für ein Werkzeug, das überall
            gilt. Am Telefon steht es rechts in der festen Leiste. */}
        <div className="haus-werkzeug">
          {/* **Am Telefon steht die Suche unten** (19.09.2026), als Fach der
              Leiste (`handywege.ts`); die Lupe fällt dort weg, wo es das Fach
              gibt, und bleibt, wo es fehlt. Am Schreibtisch steht sie mittig
              (oben). */}
          {telefon && !handywege.some((p) => p.weg === "/suche") ? <SucheKnopf /> : null}
          {/* **Nur, wer Preise sehen darf** (A-02): Der Kranfahrer las im
              Knopftitel „was es kostet" und bekam dann nur den Satz, dass die
              Auskunft zum Vertrieb gehört. */}
          {darfAuf("/nachschlagen") ? (
          <button
            type="button"
            className="kopf-nachschlagen"
            onClick={nachschlagenUmschalten}
            /* **Nur das Zeichen** (18.09.2026). Luca: *„Das ist jetzt
               natürlich total irreführend, weil kein Mensch weiß, was er
               suchen soll und was er nachschlagen soll."* Was die beiden
               trennt, ist der Euro im Zeichen (`bausteine/Zeichen.tsx`). Das
               Wort steht in `title` und `aria-label`. */
            title={t("nachschlagen.kuerzel")}
            aria-label={t("nachschlagen.kuerzel")}
            aria-keyshortcuts="Alt+N"
          >
            <Zeichen name="nachschlagen" />
          </button>
          ) : null}
          {telefon ? null : <KopfAktualisieren />}
          {telefon ? null : <KopfHilfe />}
          {/* **Die Glocke** (24.09.2026, 51-meldungen.md): was sich für mich
              geändert hat — über jeder Maske. */}
          <Glocke />
          {telefon ? null : <Benutzermenue />}
        </div>
      </header>

      <div className="app" data-einrichten={richtetEin ? "ja" : "nein"}>
        {telefon ? null : (
          <Leiste
            offen={leisteOffen}
            schliessen={schliessen}
            einrichten={richtetEin}
            zielMerken={leisteMerken}
          />
        )}
        {/* Die `id` ist das Portalziel des Deckblatts (bausteine/Deckblatt.tsx):
            Ein Blatt, das sich über die Maske legt, füllt genau diesen
            Bereich — die Seitenleiste daneben bleibt sichtbar. */}
        <div className="maskenraum" id="maskenraum" data-hleiste={handywege.length ? "ja" : "nein"}>
          {/* **Am Telefon steht der Maskenkopf hier** und nicht in der
              Kopfzeile (Begründung dort). Er ist das erste, was unter der
              festen Leiste steht, und er bekommt die volle Breite. Trägt eine
              Maske keinen `<Kopf>`, bleibt das Element leer und zeichnet
              nichts (`.kopfziel:empty`, `stile/telefon.css`). */}
          <div id="kopfziel" className="kopfziel" ref={kopfMerken} />
          <NebenleisteRaum ziel={leisteZiel} beiWahl={schliessen}>
            <KopfRaum ziel={kopfZiel}>
              {/* **Nur lesen** (0274): Der Betrieb ist nach Vertragsende auf
                  Lesen gestellt (R3), oder dieser Mensch ist Prüfer (B7).
                  Das Band sagt es vorher; der Riegel ist die API. */}
              {ich?.nurLesen ? (
                <div className="nurlesen-band" role="status">
                  <Meldung art="acht" titel={`nurLesen.${ich.nurLesen}Titel`}>
                    <p><T k={`nurLesen.${ich.nurLesen}Satz`} /></p>
                  </Meldung>
                </div>
              ) : null}
              {gesperrt ? null : <Outlet />}
            </KopfRaum>
          </NebenleisteRaum>
          <Handyleiste punkte={handywege} />
          {/* Das Blatt hinter dem Kreis in der Mitte — ebenfalls nur, wo es
              die Leiste gibt (`Anlegeblatt.tsx`). */}
          {handywege.length ? <Anlegeblatt /> : null}
          {/* Das Nachschlagen und die Merkliste bleiben hier — sie gehören
              in den Arbeitsbereich. Das Band zum Aktualisieren nicht mehr:
              Es steht jetzt am Ende der Schale (siehe unten). */}
          {/* **Das Nachschlagen — von überall, ohne die Maske zu verlassen.**
              Es steht hier aus demselben Grund wie die Merkliste: Es gehört
              der Anwendung und keiner Maske. Wer in der Kundenakte sitzt und
              am Telefon gefragt wird, was der Bagger nächste Woche kostet,
              darf die Akte nicht verlieren — und das Deckblatt legt sich
              genau über diesen Bereich, die Seitenleiste daneben bleibt
              stehen. Aufgemacht wird es von der Leiste und vom Tastenkürzel
              (`schale/nachschlagen.ts`); zu ist es ein Nichts und fragt
              nichts ab. */}
          <Nachschlagen />
          {/* **Das Fenster „Ortsaufnahme anlegen"** (08.10.2026, Paket
              ortsaufnahme) — von „Neu", aus Angebot und Auftrag und aus der
              Liste, ohne die Maske zu verlassen (`schale/ortsaufnahme.ts`).
              Am Telefon ist es das Blatt von unten. Zu zeichnet es nichts. */}
          <OrtsaufnahmeFenster />
          {/* Der Warenkorb der Preisauskunft. Er steht hier und nicht in
              einer Maske, weil Luca ihn am 17.09.2026 "generell" verlangt
              hat: Wer nach der dritten Auskunft ins Brett wechselt, findet
              seine Posten dort wieder. Leer zeichnet er nichts. */}
          <Merkliste />
          {/* Das Fenster, wenn etwas nicht in die Sammlung darf (19.09.2026) —
              einmal hier, weil an sechs Stellen gemerkt wird. */}
          <Merksperre />
          {/* Der Mitschrieb fürs Telefon — aus, bis ihn jemand unter „Mehr"
              anschaltet (20.09.2026, `bausteine/Touchdiagnose.tsx`). */}
          <Touchdiagnose />
        </div>
        {/* Runterziehen zum Aktualisieren (`bausteine/Ziehauffrischen.tsx`).
            Die Geste horcht am Arbeitsbereich — das **Band** aber gehört an
            den oberen Rand des Schirms und nicht in den Arbeitsbereich.

            Luca, 18.09.2026: *„Das Aktualisieren, dieses Runterziehen, das
            muss von ganz oben kommen. Das kommt jetzt irgendwie aus der
            Header-Leiste raus."* Genau das tat es: Bis hierher stand das
            Band mit `top: 0` im `.maskenraum`, und der beginnt unter der
            Kopfzeile. Es erschien also **unter** dem Kopf, als schöbe der
            Kopf es aus sich heraus. Jetzt liegt es fest am Schirm
            (`stile/ziehauffrischen.css`) und fährt über die Kopfzeile
            herunter, wie man es vom Telefon kennt.

            Es steht nur am Telefon, nur in der installierten PWA und nur,
            solange sich eine Maske mit `useZiehauffrischen` eingehängt
            hat; sonst ist hier nichts. */}
        <Ziehauffrischen />
      </div>
    </div>
  );
}

/* ── Die eingeklappte Seitenleiste merken ─────────────────────────────
   `localStorage`: eine Bequemlichkeit je Gerät, kein Datenbestand. Geht
   der Eintrag verloren, steht die Leiste wieder offen — und mehr passiert
   nicht. */
const EINGEKLAPPT = "machine-office:leiste-eingeklappt";

function leisteEingeklappt(): boolean {
  try {
    return localStorage.getItem(EINGEKLAPPT) === "1";
  } catch {
    return false;
  }
}

function leisteEingeklapptMerken(an: boolean): void {
  try {
    if (an) localStorage.setItem(EINGEKLAPPT, "1");
    else localStorage.removeItem(EINGEKLAPPT);
  } catch {
    /* Ohne Speicher gilt die Wahl bis zum Neuladen. */
  }
}
